import { Link } from 'react-router-dom';
import type { Drama } from '@/types/content';

const WEEK_LABELS = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

interface DramaHeroProps {
  drama: Drama;
}

export default function DramaHero({ drama }: DramaHeroProps) {
  const progress = Math.round((drama.updatedEpisodes / drama.totalEpisodes) * 100);
  const isFinished = drama.status === '已完结';

  return (
    <section className="w-full border-b border-background-200 bg-background-100 py-8 md:py-12">
      <div className="mx-auto w-full max-w-content px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <div className="h-[420px] w-full max-w-[300px] overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-[440px]">
              <img
                src={drama.cover}
                alt={`短剧《${drama.title}》海报`}
                title={`${drama.title} 连续剧集更新顺序与分集剧情介绍`}
                className="h-full w-full object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-9">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-md px-2.5 py-1 text-xs ${
                  isFinished ? 'bg-secondary-900 text-background-100' : 'bg-primary-500 text-background-50'
                }`}
              >
                {drama.status}
              </span>
              <Link
                to={`/dramas?category=${encodeURIComponent(drama.category)}`}
                className="cursor-pointer rounded-md bg-accent-100 px-2.5 py-1 text-xs text-accent-900 transition-colors hover:bg-accent-200"
              >
                {drama.category}
              </Link>
              {drama.tags.map((tag) => (
                <span key={tag} className="rounded-md bg-background-200 px-2.5 py-1 text-xs text-foreground-700">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
              《{drama.title}》
            </h1>
            <p className="mt-2 text-sm text-foreground-600 md:text-base">{drama.subtitle}</p>

            <dl className="mt-6 grid grid-cols-2 gap-4 border-y border-background-200 py-5 sm:grid-cols-4">
              <div>
                <dt className="text-xs text-foreground-500">评分</dt>
                <dd className="font-display mt-1 flex items-center gap-1 text-xl text-foreground-950">
                  {drama.rating.toFixed(1)}
                  <i className="ri-star-fill text-sm text-accent-500" aria-hidden="true" />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">热度</dt>
                <dd className="font-display mt-1 text-xl text-foreground-950">
                  {(drama.heat / 10000).toFixed(1)}
                  <span className="ml-0.5 text-xs text-primary-600">万</span>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">已更新</dt>
                <dd className="font-display mt-1 text-xl text-foreground-950">
                  {drama.updatedEpisodes}
                  <span className="ml-0.5 text-xs text-foreground-500">/ {drama.totalEpisodes} 集</span>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">更新节奏</dt>
                <dd className="mt-1 text-xs leading-6 text-foreground-800">{drama.schedule}</dd>
              </div>
            </dl>

            <div className="mt-6">
              <div className="flex items-center justify-between text-xs text-foreground-600">
                <span>更新进度</span>
                <span className="text-accent-700">{progress}%</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {drama.scheduleDays.length > 0 && (
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">每周更新：</span>
                {drama.scheduleDays.map((day) => (
                  <span
                    key={day}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                  >
                    {WEEK_LABELS[day - 1]}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="#episodes"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-list-ordered" aria-hidden="true" />
                查看分集剧情介绍
              </a>
              <a
                href="#synopsis"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                <i className="ri-book-open-line" aria-hidden="true" />
                全季剧情梗概
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}